import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Coffee, Maximize2, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { dailyFocusBlocksApi, planApi, workItemsApi } from '../api/services';
import { DailyFocusBlock, WorkItem } from '../types/models';
import { useAuth } from '../auth/AuthProvider';
import { openFocusSession } from './focus/FocusSessionFull';
import { useDecisionCapture } from './workItems/useDecisionCapture';
import { useToast } from './ui/toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// FocusSessionCard — pinned bottom-right "you're inside a focus block right
// now" widget that appears on every page. Mirrors the Mac HUD's intent:
//
//   * Shows the active block + the work items planned for it
//   * Per-item Complete + Defer-to-tomorrow
//   * "Take a break" → 5-min chips up to 60 min → countdown + soft chime
//
// Polls /dailyfocusblocks every 60s (cheap call, low traffic) so the card
// appears/disappears in sync with the block clock. Mounted in AppLayout
// alongside <Outlet /> so it survives route transitions without remounting.
// ---------------------------------------------------------------------------

const BREAK_DURATIONS = [5, 10, 15, 20, 25, 30, 45, 60];
const POLL_INTERVAL_MS = 60_000;

function minuteOfDay(hhmmss: string): number {
  const [h = '0', m = '0'] = hhmmss.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function currentMinuteOfDay(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function tomorrowStr(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface FocusSessionCardProps {
  /**
   * Anchor corner for the HUD. Defaults to `bottom-right` for the
   * web SPA. The Mac shell sets `window.cadenceHudPosition = 'top-right'`
   * so the HUD floats under the system menu bar there (matches the
   * redesign's Cadence Mac.html spec). Pass an explicit prop to
   * override the global (useful for tests).
   */
  position?: 'bottom-right' | 'top-right';
}

function defaultHudPosition(): 'bottom-right' | 'top-right' {
  if (typeof window !== 'undefined') {
    const flag = (window as any).cadenceHudPosition;
    if (flag === 'top-right' || flag === 'bottom-right') return flag;
  }
  return 'bottom-right';
}

export default function FocusSessionCard({ position }: FocusSessionCardProps = {}) {
  const { isAuthenticated } = useAuth();
  // Resolve the anchor once per mount — re-reading on every render
  // would let a late `window.cadenceHudPosition` flip cause layout
  // thrash without re-mounting the card.
  const [anchor] = useState<'bottom-right' | 'top-right'>(() => position ?? defaultHudPosition());
  const anchorClass = anchor === 'top-right' ? 'top-4 right-4' : 'bottom-4 right-4';
  const [blocks, setBlocks] = useState<DailyFocusBlock[]>([]);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [breakEndsAt, setBreakEndsAt] = useState<number | null>(null);
  const [breakTotal, setBreakTotal] = useState<number>(0);
  const [now, setNow] = useState<number>(Date.now());
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Find the block we're inside right now (if any). v2.2.3 — read minute-of-day
  // from the ticking `now` state directly instead of calling currentMinuteOfDay()
  // so the linter sees `now` as a real dep (was flagged as unused before — the
  // re-trigger was the intent but the closure didn't reference it).
  const activeBlock = useMemo(() => {
    const d = new Date(now);
    const m = d.getHours() * 60 + d.getMinutes();
    return blocks.find(b => minuteOfDay(b.startTime) <= m && minuteOfDay(b.endTime) > m) ?? null;
  }, [blocks, now]);

  // Today's focus blocks: once a minute while the tab is visible, and again
  // when it becomes visible (a hidden tab polled all day for nobody). Card is
  // best-effort UI, so a failed read keeps what it has.
  const fetchBlocks = useCallback(async () => {
    if (!isAuthenticated || document.visibilityState !== 'visible') return;
    try {
      setBlocks((await dailyFocusBlocksApi.getByDate(todayStr())) ?? []);
    } catch { /* keep the last blocks */ }
  }, [isAuthenticated]);

  // The top of today's saved plan, capped at 3 so the card stays compact: the
  // same order as the dashboard, the Mac HUD and the email. Read only while a
  // block is on (it's all the card shows), and when the plan changes.
  const fetchItems = useCallback(async () => {
    try {
      const plan = await planApi.getToday();
      setItems((plan?.items ?? []).slice(0, 3));
    } catch { /* keep the last items */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    void fetchBlocks();
    const id = window.setInterval(() => { void fetchBlocks(); }, POLL_INTERVAL_MS);
    const onVisible = () => { void fetchBlocks(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, fetchBlocks]);

  const activeBlockId = activeBlock?.id ?? null;
  useEffect(() => {
    if (!activeBlockId) return;
    void fetchItems();
    const onPlanChanged = () => { void fetchItems(); };
    window.addEventListener('cadence:plan-changed', onPlanChanged);
    return () => window.removeEventListener('cadence:plan-changed', onPlanChanged);
  }, [activeBlockId, fetchItems]);

  // A 1 s tick during a block or a break (the countdowns); otherwise 15 s is
  // enough to notice a block starting.
  const fastTick = activeBlockId !== null || breakEndsAt !== null;
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), fastTick ? 1000 : 15_000);
    return () => window.clearInterval(id);
  }, [fastTick]);

  const { toast } = useToast();
  // A Decision asks what was decided before it's done (useDecisionCapture),
  // as on Work Items; then the card re-reads the plan.
  const { completeWorkItem, dialog: decisionDialog } = useDecisionCapture(
    fetchItems,
    message => toast({ variant: 'error', message }),
  );

  // Detect break end and fire the chime + bring the tab forward.
  useEffect(() => {
    if (breakEndsAt && now >= breakEndsAt) {
      audioRef.current?.play().catch(() => { /* user may not have interacted yet */ });
      setBreakEndsAt(null);
    }
  }, [now, breakEndsAt]);

  if (!isAuthenticated || !activeBlock) return decisionDialog ?? null;
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className={cn(
          'fixed z-50 flex items-center gap-2 rounded-full bg-primary/90 px-3 py-2 text-sm font-medium text-primary-foreground shadow-lg backdrop-blur-md hover:bg-primary',
          anchorClass,
        )}
        title="Show focus session"
      >
        <span className="size-2 rounded-full bg-white animate-pulse" />
        In focus
      </button>
    );
  }

  const startBreak = (minutes: number) => {
    setBreakTotal(minutes * 60_000);
    setBreakEndsAt(Date.now() + minutes * 60_000);
  };

  const cancelBreak = () => setBreakEndsAt(null);

  // Off the card at once; on failure, back where it was, with a message.
  const optimistic = async (item: WorkItem, write: () => Promise<unknown>, failed: string) => {
    const at = items.findIndex(i => i.id === item.id);
    setItems(prev => prev.filter(i => i.id !== item.id));
    try {
      await write();
    } catch {
      setItems(prev => {
        if (prev.some(i => i.id === item.id)) return prev;
        const next = [...prev];
        next.splice(Math.max(0, Math.min(at, next.length)), 0, item);
        return next;
      });
      toast({ variant: 'error', message: failed });
    }
  };

  const completeItem = async (item: WorkItem) => {
    if (item.type === 'Decision') {
      await completeWorkItem(item);
      return;
    }
    await optimistic(item, () => workItemsApi.update(item.id, { status: 'Done' }), "Couldn't complete that. Try again.");
  };

  const deferItem = (item: WorkItem) =>
    optimistic(item, () => workItemsApi.update(item.id, { targetDate: tomorrowStr() }), "Couldn't defer that. Try again.");

  // Block geometry for the progress bar.
  const startM = minuteOfDay(activeBlock.startTime);
  const endM = minuteOfDay(activeBlock.endTime);
  const remainingMin = Math.max(0, endM - currentMinuteOfDay());
  const elapsedFrac = (currentMinuteOfDay() - startM) / Math.max(1, endM - startM);

  // Break geometry (when active).
  const breakRemainingMs = breakEndsAt ? Math.max(0, breakEndsAt - now) : 0;
  const breakElapsedFrac = breakEndsAt && breakTotal > 0 ? 1 - breakRemainingMs / breakTotal : 0;
  const breakMm = Math.floor(breakRemainingMs / 60_000);
  const breakSs = Math.floor((breakRemainingMs % 60_000) / 1000);

  return (
    <>
      {decisionDialog}
      {/* Pre-loaded chime. Built-in audio file shipped under /sounds; falls
          back to a tone via an inline data-url if the file 404s so the
          break-end signal still fires. */}
      <audio
        ref={audioRef}
        src="/sounds/chime.mp3"
        preload="auto"
        onError={() => {
          if (audioRef.current) {
            // Tiny data-url click as a fallback so a missing asset doesn't
            // silently break the break-end alert.
            audioRef.current.src =
              'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAESsAAABAAgAZGF0YQAAAAA=';
          }
        }}
      />

      <div
        className={cn(
          'fixed z-50 w-[340px] rounded-2xl border border-white/15 p-4 shadow-2xl backdrop-blur-2xl backdrop-saturate-150 dark',
          anchorClass,
        )}
        // Frosted glass — ~70% opaque surface lets the page bleed
        // through rather than landing as a solid panel. Matches the
        // redesign's "frosted translucent glass, pinned" treatment.
        style={{ background: 'rgba(29, 41, 57, 0.72)' }}
      >
        <div className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-full bg-primary/25">
            <span className="size-2 rounded-full bg-[#7DB1FF] animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-white/40">Focus</p>
            <p className="truncate text-sm font-semibold text-white">{activeBlock.label || 'Deep work'}</p>
          </div>
          <button
            onClick={() => openFocusSession()}
            className="text-white/40 hover:text-white"
            title="Expand to full screen"
          >
            <Maximize2 className="size-4" />
          </button>
          <button
            onClick={() => setCollapsed(true)}
            className="text-white/40 hover:text-white"
            title="Collapse"
          >
            <X className="size-4" />
          </button>
        </div>

        {breakEndsAt ? (
          <div className="mt-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-white/40">On a break</p>
            <p className="font-mono text-2xl font-semibold tabular-nums text-white">
              {breakMm}:{String(breakSs).padStart(2, '0')}
            </p>
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-[#7DB1FF]"
                style={{ width: `${Math.round(breakElapsedFrac * 100)}%` }}
              />
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={cancelBreak}>
              End break now
            </Button>
            <p className="text-[11px] text-white/40">
              A soft chime sounds when the timer runs out.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-3 flex items-baseline justify-between">
              <p className="text-base font-semibold text-white">
                {remainingMin === 0 ? 'Wrapping up' : `${remainingMin}m left`}
              </p>
              <p className="text-[11px] text-white/40">
                {String(Math.floor(startM / 60)).padStart(2, '0')}:
                {String(startM % 60).padStart(2, '0')}
                {' – '}
                {String(Math.floor(endM / 60)).padStart(2, '0')}:
                {String(endM % 60).padStart(2, '0')}
              </p>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-[#7DB1FF]"
                style={{ width: `${Math.round(Math.max(0, Math.min(1, elapsedFrac)) * 100)}%` }}
              />
            </div>

            {items.length > 0 && (
              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="mb-1 text-[10px] uppercase tracking-wider text-white/40">Working on</p>
                <ul className="space-y-1">
                  {items.map(item => (
                    <li key={item.id} className="group flex items-center gap-2">
                      <button
                        onClick={() => completeItem(item)}
                        className="flex size-4 items-center justify-center rounded-full border border-white/30 text-transparent hover:border-emerald-400 hover:text-emerald-400"
                        title="Mark done"
                      >
                        ✓
                      </button>
                      <span className="flex-1 truncate text-xs text-white/90">{item.title}</span>
                      <button
                        onClick={() => deferItem(item)}
                        className="text-white/40 opacity-0 transition-opacity group-hover:opacity-100 hover:text-white"
                        title="Defer to tomorrow"
                      >
                        <ArrowRight className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 border-t border-white/10 pt-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-start text-white/70 hover:text-white">
                    <Coffee className="mr-2 size-3.5" />
                    Take a break
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="end"
                  // Matches the parent HUD's frosted slate surface
                  // (rgba(29,41,57,0.72) + 2xl blur) so the popover
                  // reads as part of the same glass card rather than
                  // a solid dark pill that doesn't blend in either
                  // theme.
                  className="w-[200px] border-white/15 p-2 backdrop-blur-2xl backdrop-saturate-150"
                  style={{ background: 'rgba(29, 41, 57, 0.72)' }}
                >
                  <div className="grid grid-cols-4 gap-1">
                    {BREAK_DURATIONS.map(min => (
                      <button
                        key={min}
                        onClick={() => startBreak(min)}
                        className="rounded-md px-2 py-1 text-xs text-white/85 hover:bg-white/10"
                      >
                        {min}m
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>
    </>
  );
}
